import React, { useState } from "react";
import { 
  Box, 
  Button, 
  Drawer, 
  TextField, 
  Typography,
  Alert,
  CircularProgress,
  Card,
  IconButton,
  Grid,
  Snackbar,
  Chip,
  Pagination,
  InputAdornment,
  Tabs,
  Tab,
  Stack,
  Avatar,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { 
  Delete, 
  Edit, 
  Add, 
  Close, 
  Search, 
  FilterList, 
  ArrowBack,
  Image as ImageIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListSubBannersQuery,
  useAddSubBannerMutation,
  useUpdateSubBannerMutation,
  useDeleteSubBannerMutation,
  SubBanner as SubBannerType,
} from '../../features/admin/subBanner.ts';

const SubBanner = () => {
  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<SubBannerType | null>(null);
  const [formData, setFormData] = useState({
    caption: '',
    title: '',
    start_date: '',
    end_date: '',
    active: 1,
    cta: '',
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  // API hooks
  const { 
    data, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListSubBannersQuery();

  const [addSubBanner, { isLoading: isAdding }] = useAddSubBannerMutation();
  const [updateSubBanner, { isLoading: isUpdating }] = useUpdateSubBannerMutation();
  const [deleteSubBanner, { isLoading: isDeleting }] = useDeleteSubBannerMutation();

  const subBanners = data?.banners || [];
  
  // Filter to show only sub-banners (mode === 'sub')
  const filteredSubBanners = subBanners.filter((banner: SubBannerType) => 
    banner.mode === 'sub' || banner.mode === 'Sub'
  );

  const bannersPerPage = 10;
  const totalRecords = filteredSubBanners.length;
  const totalPages = Math.ceil(totalRecords / bannersPerPage);
  const isEditing = Boolean(editingBanner);

  // Helper function to get the correct image path
  const getImageUrl = (banner: SubBannerType) => {
    const possibleFields = [
      'banner_image',
      'file_path', 
      'filename',
      'image',
      'file',
      'path',
      'image_path',
      'banner_image_path'
    ];
    
    let imagePath = null;
    
    for (const field of possibleFields) {
      if (banner[field as keyof SubBannerType]) {
        imagePath = banner[field as keyof SubBannerType] as string;
        break;
      }
    }
    
    if (!imagePath) {
      return '';
    }
    
    let cleanPath = imagePath.trim();
    
    if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
      return cleanPath;
    }
    
    if (cleanPath.includes('/')) {
      const parts = cleanPath.split('/');
      cleanPath = parts[parts.length - 1];
    }
    
    if (cleanPath.includes('\\')) {
      const parts = cleanPath.split('\\');
      cleanPath = parts[parts.length - 1];
    }
    
    const baseUrl = 'https://fftestservice.sarsetech.com';
    const imageUrl = `${baseUrl}/images/${cleanPath}`;
    
    return imageUrl;
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
  };

  const resetForm = () => {
    setFormData({
      caption: '',
      title: '',
      start_date: '',
      end_date: '',
      active: 1,
      cta: '',
    });
    setSelectedImage(null);
    setImagePreview('');
    setFormErrors({});
    setEditingBanner(null);
  };

  const handleAddBanner = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEditBanner = (banner: SubBannerType) => {
    setFormData({
      caption: banner.caption || '',
      title: banner.title || '',
      start_date: banner.start_date.split('T')[0],
      end_date: banner.end_date.split('T')[0],
      active: banner.active === 1 || banner.active === '1' || banner.active === true ? 1 : 0,
      cta: banner.cta || '',
    });
    setImagePreview(banner.banner_image ? 
      `https://fftestservice.sarsetech.com/images/${banner.banner_image}` : '');
    setEditingBanner(banner);
    setShowForm(true);
  };

  const handleBackToList = () => {
    setShowForm(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleStatusChange = (e: any) => {
    setFormData(prev => ({ ...prev, active: Number(e.target.value) }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setSnackbar({
          open: true,
          message: 'Please select a valid image file (JPEG, PNG, or WebP)',
          severity: 'error'
        });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: 'Image size should be less than 2MB',
          severity: 'error'
        });
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.caption.trim()) {
      newErrors.caption = 'Caption is required';
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }
    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }
    if (!formData.cta.trim()) {
      newErrors.cta = 'CTA URL is required';
    }
    if (!isEditing && !selectedImage) {
      newErrors.image = 'Image is required';
    }
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsFormSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('caption', formData.caption);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('start_date', formData.start_date);
      formDataToSend.append('end_date', formData.end_date);
      formDataToSend.append('active', formData.active ? '1' : '0');
      formDataToSend.append('cta', formData.cta);
      formDataToSend.append('mode', 'sub'); // IMPORTANT: Set mode to 'sub' for sub-banners
      
      if (selectedImage) {
        formDataToSend.append('banner_image', selectedImage);
      }

      if (isEditing && editingBanner) {
        await updateSubBanner({ id: editingBanner.id, formData: formDataToSend }).unwrap();
      } else {
        await addSubBanner(formDataToSend).unwrap();
      }
      
      setSnackbar({
        open: true,
        message: `Sub-banner ${isEditing ? 'updated' : 'added'} successfully!`,
        severity: 'success'
      });
      
      handleBackToList();
      refetch();
    } catch (error: any) {
      console.error('Submit error:', error);
      setSnackbar({
        open: true,
        message: error.data?.message || error.message || `Error ${isEditing ? 'updating' : 'adding'} sub-banner.`,
        severity: 'error'
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleDeleteBanner = async (bannerId: number) => {
    if (window.confirm('Are you sure you want to delete this sub-banner?')) {
      try {
        await deleteSubBanner(bannerId).unwrap();
        setSnackbar({
          open: true,
          message: 'Sub-banner deleted successfully!',
          severity: 'success'
        });
        refetch();
      } catch (err: any) {
        setSnackbar({
          open: true,
          message: err?.data?.message || 'Failed to delete sub-banner',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const renderStatus = (status: number | string | boolean) => {
    if (status === 1 || status === '1' || status === true) {
      return <Chip label="Active" size="small" sx={{ bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600, fontSize: '12px' }} />;
    }
    return <Chip label="Inactive" size="small" sx={{ bgcolor: '#e4e5e7', color: '#616161', fontWeight: 600, fontSize: '12px' }} />;
  };

  const searchFilteredBanners = filteredSubBanners.filter((banner: SubBannerType) => {
    if (searchStr) {
      return banner.title?.toLowerCase().includes(searchStr.toLowerCase()) ||
             banner.caption?.toLowerCase().includes(searchStr.toLowerCase());
    }
    if (tabValue === 1) return banner.active === 1;
    if (tabValue === 2) return banner.active === 0;
    return true;
  });

  const activeBanners = filteredSubBanners.filter((b: SubBannerType) => b.active === 1).length;
  const inactiveBanners = filteredSubBanners.filter((b: SubBannerType) => b.active === 0).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={() => {}} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu mobileOpen={false} onToggle={() => {}} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            {!showForm ? (
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Sub-Banners
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleAddBanner}
                    sx={{
                      bgcolor: '#202223',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#303030' }
                    }}
                  >
                    Add sub-banner
                  </Button>
                </Box>

                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs 
                      value={tabValue} 
                      onChange={(e, v) => setTabValue(v)}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '14px',
                          minHeight: 48,
                          color: '#6d7175',
                        },
                        '& .Mui-selected': {
                          color: '#202223',
                          fontWeight: 600,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#202223',
                          height: 3,
                        }
                      }}
                    >
                      <Tab label={`All (${filteredSubBanners.length})`} />
                      <Tab label={`Active (${activeBanners})`} />
                      <Tab label={`Inactive (${inactiveBanners})`} />
                    </Tabs>
                  </Box>

                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Search sub-banners"
                      value={searchStr}
                      onChange={handleSearch}
                      fullWidth
                      sx={{ 
                        maxWidth: 400,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<FilterList />}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#c9cccf',
                        color: '#202223',
                        borderRadius: '8px',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: '#202223',
                          bgcolor: 'transparent',
                        }
                      }}
                    >
                      Filter
                    </Button>
                  </Box>

                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load sub-banners. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoading && !isError && searchFilteredBanners.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <ImageIcon sx={{ fontSize: 64, color: '#6d7175', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        No sub-banners found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr ? `No results for "${searchStr}"` : 'Get started by adding your first sub-banner'}
                      </Typography>
                    </Box>
                  )}

                  {!isLoading && !isError && searchFilteredBanners.length > 0 && (
                    <Box sx={{ bgcolor: 'white' }}>
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '50px 100px 1fr 200px 200px 120px 100px',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb'
                      }}>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          #
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Image
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Sub-Banner
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Date Range
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          CTA Link
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Status
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>
                          Actions
                        </Typography>
                      </Box>

                      {searchFilteredBanners.map((banner: SubBannerType, index: number) => (
                        <Box
                          key={banner.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '50px 100px 1fr 200px 200px 120px 100px',
                            gap: 2,
                            px: 2,
                            py: 2,
                            alignItems: 'center',
                            borderBottom: index < searchFilteredBanners.length - 1 ? '1px solid #e1e3e5' : 'none',
                            '&:hover': { bgcolor: '#f9fafb' },
                            transition: 'background-color 0.15s ease',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#6d7175' }}>
                            {(page - 1) * bannersPerPage + index + 1}
                          </Typography>

                          <Avatar
                            src={getImageUrl(banner)}
                            variant="rounded"
                            sx={{ width: 72, height: 48, bgcolor: '#f1f2f4' }}
                          >
                            <ImageIcon />
                          </Avatar>

                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223', mb: 0.5 }}>
                              {banner.title || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {banner.caption || 'No caption'}
                            </Typography>
                          </Box>

                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                            {formatDate(banner.start_date)} - {formatDate(banner.end_date)}
                          </Typography>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LinkIcon sx={{ fontSize: 14, color: '#6d7175' }} />
                            <Typography 
                              variant="caption" 
                              color="primary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 180
                              }}
                            >
                              {banner.cta || 'N/A'}
                            </Typography>
                          </Box>

                          <Box>
                            {renderStatus(banner.active)}
                          </Box>

                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditBanner(banner)}
                              sx={{ 
                                color: '#6d7175',
                                '&:hover': { bgcolor: '#f1f2f4' }
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteBanner(banner.id)}
                              disabled={isDeleting}
                              sx={{ 
                                color: '#6d7175',
                                '&:hover': { bgcolor: '#f1f2f4' }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Card>

                {totalPages > 1 && !isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handleChangePage}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '8px',
                          fontWeight: 500,
                        },
                        '& .Mui-selected': {
                          bgcolor: '#202223 !important',
                          color: 'white',
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{ 
                      color: '#202223',
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 2,
                      '&:hover': { bgcolor: 'transparent' }
                    }}
                  >
                    Sub-Banners
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    {isEditing ? 'Edit sub-banner' : 'Add sub-banner'}
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Sub-banner details
                            </Typography>
                            <TextField
                              name="title"
                              label="Sub-banner title"
                              value={formData.title}
                              onChange={handleInputChange}
                              error={!!formErrors.title}
                              helperText={formErrors.title}
                              fullWidth
                              required
                              placeholder="e.g., Up to 50% Off on Selected Items"
                              disabled={isFormSubmitting}
                              sx={{ mb: 2 }}
                            />

                            <TextField
                              name="caption"
                              label="Caption"
                              value={formData.caption}
                              onChange={handleInputChange}
                              error={!!formErrors.caption}
                              helperText={formErrors.caption}
                              fullWidth
                              required
                              placeholder="e.g., Summer Sale 2024"
                              disabled={isFormSubmitting}
                            />
                          </Box>
                        </Card>

                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Media
                            </Typography>
                            
                            <Box sx={{ 
                              border: '2px dashed #c9cccf', 
                              borderRadius: '12px', 
                              p: 4,
                              textAlign: 'center',
                              cursor: 'pointer',
                              bgcolor: '#fafbfb',
                              transition: 'all 0.2s',
                              '&:hover': { 
                                borderColor: '#202223',
                                bgcolor: '#f6f6f7'
                              }
                            }}
                            onClick={() => document.getElementById('image-upload')?.click()}>
                              {imagePreview ? (
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                  <Box
                                    component="img"
                                    src={imagePreview}
                                    sx={{ 
                                      maxWidth: '100%', 
                                      maxHeight: 200,
                                      borderRadius: '8px',
                                      objectFit: 'contain'
                                    }}
                                  />
                                  <IconButton
                                    sx={{
                                      position: 'absolute',
                                      top: -8,
                                      right: -8,
                                      bgcolor: 'white',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                      '&:hover': { bgcolor: '#f5f5f5' }
                                    }}
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedImage(null);
                                      setImagePreview('');
                                    }}
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Box>
                                  <Box sx={{ 
                                    width: 56, 
                                    height: 56, 
                                    borderRadius: '8px',
                                    bgcolor: 'white',
                                    border: '1px solid #e1e3e5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 2
                                  }}>
                                    <Add sx={{ fontSize: 24, color: '#6d7175' }} />
                                  </Box>
                                  <Button
                                    variant="outlined"
                                    sx={{
                                      textTransform: 'none',
                                      borderColor: '#c9cccf',
                                      color: '#202223',
                                      fontWeight: 500,
                                      borderRadius: '8px',
                                      '&:hover': {
                                        borderColor: '#202223',
                                        bgcolor: 'transparent',
                                      }
                                    }}
                                  >
                                    {isEditing ? 'Change image (optional)' : 'Add sub-banner image'}
                                  </Button>
                                  <Typography variant="caption" display="block" sx={{ color: '#6d7175', mt: 1 }}>
                                    Maximum 2MB. Formats: JPG, PNG, WebP
                                  </Typography>
                                  {formErrors.image && (
                                    <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                                      {formErrors.image}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                            
                            <input
                              id="image-upload"
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              onChange={handleImageChange}
                              style={{ display: 'none' }}
                              disabled={isFormSubmitting}
                            />
                          </Box>
                        </Card>

                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Schedule
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  name="start_date"
                                  label="Start Date"
                                  type="date"
                                  value={formData.start_date}
                                  onChange={handleInputChange}
                                  error={!!formErrors.start_date}
                                  helperText={formErrors.start_date}
                                  fullWidth
                                  required
                                  disabled={isFormSubmitting}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  name="end_date"
                                  label="End Date"
                                  type="date"
                                  value={formData.end_date}
                                  onChange={handleInputChange}
                                  error={!!formErrors.end_date}
                                  helperText={formErrors.end_date}
                                  fullWidth
                                  required
                                  disabled={isFormSubmitting}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </Card>

                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Call to action
                            </Typography>
                            <TextField
                              name="cta"
                              label="CTA URL"
                              value={formData.cta}
                              onChange={handleInputChange}
                              error={!!formErrors.cta}
                              helperText={formErrors.cta || "Enter the product URL where users will be redirected"}
                              fullWidth
                              required
                              placeholder="https://example.com/product"
                              disabled={isFormSubmitting}
                            />
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                        <Box sx={{ p: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                            Status
                          </Typography>
                          <FormControl fullWidth disabled={isFormSubmitting}>
                            <Select
                              value={formData.active.toString()}
                              onChange={handleStatusChange}
                              sx={{ borderRadius: '8px' }}
                            >
                              
                            </Select>
                          </FormControl>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            {formData.active === 1 ? 'Sub-banner will be visible to customers' : 'Sub-banner will be hidden from customers'}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    justifyContent: 'flex-end', 
                    mt: 3,
                    pt: 3,
                    borderTop: '1px solid #e1e3e5'
                  }}>
                    <Button
                      onClick={handleBackToList}
                      disabled={isFormSubmitting}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        color: '#202223',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Discard
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isFormSubmitting}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      {isFormSubmitting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" />
                          Saving...
                        </Box>
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubBanner;